/* eslint-disable quotes */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";

import { getConfig } from "@edx/frontend-platform";
import {
  getCountryList,
  getLocale,
  useIntl,
} from "@edx/frontend-platform/i18n";
import PropTypes from "prop-types";

import { FormFieldRenderer } from "../../field-renderer";
import { FIELDS } from "../data/constants";
import messages from "../messages";
import {
  CountryField,
  HonorCode,
  OccupationField,
  TermsOfService,
  StudiesField,
  RegionField,
  TownField,
  HighSchoolRo,
  UniversityRo,
  SchoolTeach,
} from "../RegistrationFields";

/**
 * Fields on registration page that are not the default required fields (name, email, username, password).
 * These configurable required fields are defined on the backend using REGISTRATION_EXTRA_FIELDS setting.
 *
 * Country and Honor Code/Terms of Services (if enabled) will appear at the bottom of the form, even if they
 * appear higher in order returned by backend. This is to make the user experience better.
 *
 * For edX only:
 *  Country and honor code fields are required by default, and we will continue to show them on
 *  frontend even if the API doesn't return it. The `SHOW_CONFIGURABLE_EDX_FIELDS` flag will enable
 *  it for edX.
 * */
const ConfigurableRegistrationForm = (props) => {
  const { formatMessage } = useIntl();
  const {
    email,
    fieldDescriptions,
    fieldErrors,
    formFields,
    setFieldErrors,
    setFormFields,
    autoSubmitRegistrationForm,
    roSelected,
    setRoSelected,
    handleChangeParent,
  } = props;

  /** The reason for adding the entry 'United States' is that Chrome browser aut-fill the form with the 'Unites
  States' instead of 'United States of America' which does not exist in country dropdown list and gets the user
  confused and unable to create an account. So we added the United States entry in the dropdown list.
 */
  fieldDescriptions["terms_of_service"] = {"name": "terms_of_service" };
  const otherCountryText = formatMessage(messages['registration.country.other'])
  const roCountries = {
    'RO': 'România',
    'MD': 'Moldova, Republica',
    'XX': 'Altul',
  }
  const countryList = useMemo(
    () =>
      getCountryList(getLocale()).concat([
        { code: "XX", name: otherCountryText, displayValue: otherCountryText },
      ]).filter((country) => ["RO","XX","MD"].includes(country.code)).sort((a,b) => a.code === "RO" ? -1 : 1)
      .map(country => {
        if (getLocale() === 'ro') {
          return {
            ...country,
          'displayValue': roCountries[country['code']],
           'name': roCountries[country['code']],
          }
         } else {
            return country
      }
    }),
    []
  );

  let showTermsOfServiceAndHonorCode = false;
  let showCountryField = true;

  const [showHighSchool,setShowHighSchool] = useState(false);
  const [showUniversity,setShowUniversity] = useState(false);
  const [showSchoolTeach, setShowSchoolTeach] = useState(false);

  useEffect(() => {
    if (roSelected) {
      let townCode = formFields.town ? formFields.town.selectionId : "";
      if (townCode === '') {
        setShowHighSchool(false);
        setShowUniversity(false);
        setShowSchoolTeach(false);
      }else{
      if (formFields.occupation?.selectionId === "pupil"){
        setShowHighSchool(true);
        setShowUniversity(false);
        setShowSchoolTeach(false);
      }
      if(formFields.occupation?.selectionId === "student"){
        setShowHighSchool(false);
        setShowUniversity(true);
        setShowSchoolTeach(false);

      }
      if (formFields.occupation?.selectionId === "teacher"){
        setShowHighSchool(false);
        setShowUniversity(false);
        setShowSchoolTeach(true);
      }
    }

    }else{
      let countryCode = formFields.country? formFields.country.countryCode : "";

      if(countryCode === ""){
        setShowHighSchool(false);
        setShowUniversity(false);
        setShowSchoolTeach(false);

      }else{
        if (formFields.occupation?.selectionId === "pupil"){
          setShowHighSchool(true);
          setShowUniversity(false);
          setShowSchoolTeach(false);
        }
        if(formFields.occupation?.selectionId === "student"){
          setShowHighSchool(false);
          setShowUniversity(true);
          setShowSchoolTeach(false);

        }
        if (formFields.occupation?.selectionId === "teacher"){
          setShowHighSchool(false);
          setShowUniversity(false);
          setShowSchoolTeach(true);
        }

      }

    }
  }, [roSelected,formFields.town,formFields.occupation,formFields.country]);

  const formFieldDescriptions = [];
  const honorCode = [];
  const flags = {
    showConfigurableRegistrationFields:
      getConfig().ENABLE_DYNAMIC_REGISTRATION_FIELDS,
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
  };

  /**
   * If auto submitting register form, we will check tos and honor code fields if they exist for feature parity.
   */
  useEffect(() => {
    if (autoSubmitRegistrationForm) {
      if (Object.keys(fieldDescriptions).includes(FIELDS.HONOR_CODE)) {
        setFormFields((prevState) => ({
          ...prevState,
          [FIELDS.HONOR_CODE]: true,
        }));
      }
      // this need to be changed later, currenly we are using terms of service
      // as data place for saving markeing opt in checkbox
      if (Object.keys(fieldDescriptions).includes(FIELDS.TERMS_OF_SERVICE) || true) {
        setFormFields((prevState) => ({
          ...prevState,
          [FIELDS.TERMS_OF_SERVICE]: true,
        }));
      }
    }
  }, [autoSubmitRegistrationForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleErrorChange = (fieldName, error) => {
    if (fieldName) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: error,
      }));
    }
  };

  const handleOnChange = (event, countryValue = null) => {
    const { name } = event.target;
    if(event.target.name === "studies"){
      setFieldErrors((prevErrors) => ({ ...prevErrors, studies: "" }))
    }
    if (event.target.name === "region"){
      setFormFields((prevErrors) => ({ ...prevErrors, town: "" }))
    }
    let value;
    if (countryValue) {
      value = { ...countryValue };
    } else {
      value =
      event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      if (event.target.type === "checkbox") {
        setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
    }
    setFormFields((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleOnBlur = (event) => {
    const { name, value } = event.target;
    let error = "";
    if ((!value || !value.trim()) && fieldDescriptions[name]?.error_message) {
      error = fieldDescriptions[name].error_message;
    } else if (name === "confirm_email" && value !== email) {
      error = formatMessage(messages["email.do.not.match"]);
    }
    setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleOnFocus = (event) => {
    const { name } = event.target;
    setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  if (flags.showConfigurableRegistrationFields) {
    Object.keys(fieldDescriptions).forEach((fieldName) => {
      const fieldData = fieldDescriptions[fieldName];
      switch (fieldData.name) {
        case FIELDS.COUNTRY:
          showCountryField = true;
          break;
        case FIELDS.HONOR_CODE:
          if (fieldData.type === "tos_and_honor_code") {
            showTermsOfServiceAndHonorCode = true;
          } else {
            honorCode.push(
              <span key={fieldData.name}>
                <HonorCode
                  fieldType={fieldData.type}
                  value={formFields[fieldData.name]}
                  onChangeHandler={handleOnChange}
                  errorMessage={fieldErrors[fieldData.name]}
                />
              </span>
            );
          }
          break;
        case FIELDS.TERMS_OF_SERVICE:
          honorCode.push(
            <span key={fieldData.name}>
              <TermsOfService
                value={formFields[fieldData.name]}
                onChangeHandler={handleOnChange}
                errorMessage={fieldErrors[fieldData.name]}
              />
            </span>
          );
          break;
        default:
          formFieldDescriptions.push(
            <span key={fieldData.name}>
              <FormFieldRenderer
                fieldData={fieldData}
                value={formFields[fieldData.name]}
                onChangeHandler={handleOnChange}
                handleBlur={handleOnBlur}
                handleFocus={handleOnFocus}
                errorMessage={fieldErrors[fieldData.name]}
                isRequired
              />
            </span>
          );
      }
    });
  }

  formFieldDescriptions.push(
    <OccupationField
      onFocusHandler={handleOnFocus}
      selectedOccupation={formFields.occupation || "occupation.placeholder"}
      errorMessage={fieldErrors.occupation || ""}
      onChangeHandler={handleOnChange}
    />
  );

  if (flags.showConfigurableEdxFields || showCountryField) {
    formFieldDescriptions.push(
      <span key="country">
        <CountryField
          countryList={countryList}
          selectedCountry={formFields.country}
          setRoSelected={setRoSelected}
          errorMessage={fieldErrors.country || ""}
          onChangeHandler={handleOnChange}
          handleErrorChange={handleErrorChange}
          onBlurHandler={handleOnBlur}
          onFocusHandler={handleOnFocus}
          locale={getLocale()}
        />
      </span>
    );
  }

  if (roSelected) {
    formFieldDescriptions.push(
      <RegionField
       onFocusHandler={handleOnFocus}
        onChangeHandler={handleOnChange}
        errorMessage={fieldErrors.region || ""}
        selectedRegion={formFields.region}
        occupation={formFields.occupation?.selectionId}
      />
    );
  }

  if (roSelected && (formFields.region))  {
    if (formFields.region.selectionId !==''){
    formFieldDescriptions.push(
      <TownField
        onFocusHandler={handleOnFocus}
        onChangeHandler={handleOnChange}
        errorMessage={fieldErrors.town || ""}
        selectedRegion={formFields.region?.selectionValue || ""}
        selectedTown={formFields.town}
        occupation={formFields.occupation?.selectionId}
      />
    );
  }
}

  if (formFields.occupation?.selectionId === "pupil" && showHighSchool) {
    formFieldDescriptions.push(
      <HighSchoolRo
        onFocusHandler={handleOnFocus}
        selectedTown={formFields.town || ""}
        selectedRegion={formFields.region || ""}
        selectedHighSchool={formFields.highscoolro || ""}
        onChangeHandler={handleOnChange}
        errorMessage={fieldErrors.highscoolro || ""}
        roSelected={roSelected}
      />
    );
  }
  if(formFields.occupation?.selectionId === "teacher"  && showSchoolTeach){
    // Where they teach school, if they are romanain, it should be filled from list
    // otherwise it should be a freetext,
    // On success of creating new account this field should be saved on questionner table
    formFieldDescriptions.push( <SchoolTeach
    onFocusHandler={handleOnFocus}
    selectedTown={formFields.town || ""}
    selectedRegion={formFields.region || ""}
    selectedSchoolTeach={formFields.school_teach || ""}
    onChangeHandler={handleOnChange}
    roSelected={roSelected}
    errorMessage={fieldErrors.school_teach || ""}
    isTeacher={formFields.occupation?.selectionId === "teacher"}
  />
    )
  }

  if (
    formFields.occupation?.selectionId === "student" && showUniversity
  ) {
    formFieldDescriptions.push(
      <UniversityRo
        onFocusHandler={handleOnFocus}
        selectedTown={formFields.town || ""}
        selectedRegion={formFields.region || ""}
        selectedUniversity={formFields.universityro || ""}
        onChangeHandler={handleOnChange}
        roSelected={roSelected}
        errorMessage={fieldErrors.universityro || ""}
        isTeacher={formFields.occupation?.selectionId === "teacher"}
      />
    );
  }

  /*
  No need to have studies for teacher
  if (formFields.occupation?.selectionId === "teacher") {
    formFieldDescriptions.push(
      <StudiesField
        selectedStudies={formFields.studies || []}
        handleChange={handleOnChange}
        errorMessage={fieldErrors.studies || ""}
        onFocusHandler={handleOnFocus}
      />
    );
  }
   */

  if (flags.showMarketingEmailOptInCheckbox) {
    formFieldDescriptions.push(
      <span key="marketing_email_opt_in">
        <FormFieldRenderer
          fieldData={{
            type: "checkbox",
            label: formatMessage(messages["registration.opt.in.label"], {
              siteName: getConfig().SITE_NAME,
            }),
            name: "marketingEmailsOptIn",
          }}
          value={formFields.marketingEmailsOptIn}
          className="form-field--checkbox"
          onChangeHandler={handleOnChange}
          handleBlur={handleOnBlur}
          handleFocus={handleOnFocus}
        />
      </span>
    );
  }

  if (flags.showConfigurableEdxFields || showTermsOfServiceAndHonorCode) {
    formFieldDescriptions.push(
      <span key="honor_code">
        <HonorCode
          fieldType="tos_and_honor_code"
          onChangeHandler={handleOnChange}
          value={formFields.honor_code}
        />
      </span>
    );
  }
  return (
    <>
      {formFieldDescriptions}
      <div>{honorCode}</div>
    </>
  );
};

ConfigurableRegistrationForm.propTypes = {
  email: PropTypes.string.isRequired,
  fieldDescriptions: PropTypes.shape({}),
  fieldErrors: PropTypes.shape({
    country: PropTypes.string,
  }).isRequired,
  formFields: PropTypes.shape({
    country: PropTypes.shape({
      displayValue: PropTypes.string,
      countryCode: PropTypes.string,
    }),
    honor_code: PropTypes.bool,
    marketingEmailsOptIn: PropTypes.bool,
  }).isRequired,
  setFieldErrors: PropTypes.func.isRequired,
  setFormFields: PropTypes.func.isRequired,
  autoSubmitRegistrationForm: PropTypes.bool,
};

ConfigurableRegistrationForm.defaultProps = {
  fieldDescriptions: {},
  autoSubmitRegistrationForm: false,
};

export default ConfigurableRegistrationForm;
